import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSystemTheme } from '@/composables/useSystemTheme'
import { toggleAttribute } from '@/helpers/layout'

const INIT_STATE = {
	theme: 'light',
	sidenav: {
		size: 'default',
		color: 'light'
	},
	position: 'ltr-mode'
}

const MOBILE_QUERY = '(max-width: 768px)'

export const useLayout = defineStore(
	'layout',
	() => {
		const layout = ref({ ...INIT_STATE })

		const systemTheme = useSystemTheme()
		const isCustomizerOpen = ref(false)

		const isMobile = ref(false)
		const isMobileMenuOpen = ref(false)

		let viewportQuery = null

		const setTheme = (nTheme, persist = true) => {
			const resolvedTheme = nTheme === 'system' ? systemTheme.value : nTheme
			toggleAttribute('data-theme', resolvedTheme)
			if (persist) layout.value.theme = nTheme
		}

		const setSideNavColor = (nColor, persist = true) => {
			toggleAttribute('data-sidenav-color', nColor)
			if (persist) layout.value.sidenav = { ...layout.value.sidenav, color: nColor }
		}

		// offcanvas is a viewport state, never a stored preference: persisting it
		// is what made a desktop load open as a drawer after a mobile visit.
		const setSideNavSize = (nSize, persist = true) => {
			const size = nSize === 'offcanvas' ? INIT_STATE.sidenav.size : nSize

			if (persist) layout.value.sidenav.size = size
			toggleAttribute('data-sidenav-size', isMobile.value ? 'offcanvas' : size)
		}

		const openMobileMenu = () => {
			isMobileMenuOpen.value = true
			document.documentElement.classList.add('sidenav-enable')
			document.body.classList.add('overflow-hidden')
		}

		const closeMobileMenu = () => {
			isMobileMenuOpen.value = false
			document.documentElement.classList.remove('sidenav-enable')
			document.body.classList.remove('overflow-hidden')
		}

		const toggleMobileMenu = () => (isMobileMenuOpen.value ? closeMobileMenu() : openMobileMenu())

		const applyViewport = () => {
			isMobile.value = viewportQuery.matches
			setSideNavSize(layout.value.sidenav.size, false)
			if (!isMobile.value) closeMobileMenu()
		}

		// matchMedia fires only when the breakpoint is crossed. A resize
		// listener would also fire when a mobile address bar collapses on
		// scroll, which is what made the menu reopen mid-page.
		const watchViewport = () => {
			if (viewportQuery) return

			viewportQuery = window.matchMedia(MOBILE_QUERY)
			viewportQuery.addEventListener('change', applyViewport)
			applyViewport()
		}

		const setPosition = (nPosition, persist = true) => {
			toggleAttribute('dir', nPosition === 'rtl-mode' ? 'rtl' : 'ltr')
			if (persist) layout.value.position = nPosition
		}

		const toggleCustomizer = () => {
			isCustomizerOpen.value = !isCustomizerOpen.value
		}
		const customizer = { isCustomizerOpen, toggleCustomizer }

		const toggleHoverSidebar = () => {
			setSideNavSize(layout.value.sidenav.size === 'hover-active' ? 'hover' : 'hover-active')
		}

		const init = () => {
			setTheme(layout.value.theme)
			setSideNavColor(layout.value.sidenav.color)
			setSideNavSize(layout.value.sidenav.size)
			setPosition(layout.value.position)
		}

		const reset = () => {
			setTheme(INIT_STATE.theme)
			setSideNavColor(INIT_STATE.sidenav.color)
			setSideNavSize(INIT_STATE.sidenav.size)
			setPosition(INIT_STATE.position)
		}

		return {
			layout,
			isMobile,
			isMobileMenuOpen,
			setTheme,
			setSideNavColor,
			setSideNavSize,
			openMobileMenu,
			closeMobileMenu,
			toggleMobileMenu,
			watchViewport,
			setPosition,
			toggleHoverSidebar,
			isCustomizerOpen,
			toggleCustomizer,
			init,
			reset,
			customizer
		}
	},
	{
		// Sidenav size is deliberately absent: it is decided by the viewport and
		// by what the operator does this session. Storing it is what let a
		// collapsed or off-canvas sidebar follow them onto the desktop.
		persist: { pick: ['layout.theme', 'layout.sidenav.color', 'layout.position'] }
	}
)
